import React from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

const JobCard = ({ job, isSaved, onSave, onSelect, isSelected, formatTimeAgo }) => {
  const { user } = useAuth();

  const handleExternalApply = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (user) {
      const { error } = await supabase.from('job_applications').insert({
        user_id: user.id,
        job_id: job.id,
        status: 'pending',
        applied_at: new Date().toISOString()
      });

      if (error) {
        console.error("Supabase error:", error);
      }
    } else {
      // Optional: Alert if not logged in, just for debugging confirmation
      // alert("Not logged in - tracking skipped"); 
    }
    window.open(job.application_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={`group relative rounded-2xl p-6 overflow-hidden cursor-pointer transition-all duration-300 ease-in-out
        bg-white/5 dark:bg-black/90
        bg-gradient-to-br from-black/5 to-black/[0.02] dark:from-white/5 dark:to-white/[0.02]
        backdrop-blur-xl backdrop-saturate-[180%]
        border border-black/10 dark:border-white/10
        shadow-[0_8px_16px_rgb(0_0_0_/_0.15)] dark:shadow-[0_8px_16px_rgb(0_0_0_/_0.25)]
        hover:scale-[1.01] hover:shadow-xl
        ${isSelected ? 'ring-2 ring-primary-500' : ''}`}
      onClick={onSelect}
    >
      {/* Featured Gradient Glow - "Slowly Glowing Edges" */}
      {job.featured && (
        <div className="absolute inset-0 -z-10 rounded-2xl bg-gradient-to-r from-primary-500/50 via-primary-400/30 to-primary-600/50 opacity-75 blur-md animate-pulse pointer-events-none" />
      )}

      {/* Glossy Overlay Effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      {/* Corner Brackets */}
      <div className="absolute top-0 left-0 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
        <div className="absolute top-3 left-3 w-4 h-0.5 bg-primary" />
        <div className="absolute top-3 left-3 w-0.5 h-4 bg-primary" />
      </div>
      <div className="absolute bottom-0 right-0 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
        <div className="absolute bottom-3 right-3 w-4 h-0.5 bg-primary" />
        <div className="absolute bottom-3 right-3 w-0.5 h-4 bg-primary" />
      </div>

      {job.featured && (
        <div className="flex items-center space-x-1 mb-3">
          <Icon name="Star" size={16} className="text-primary-500" />
          <span className="text-xs font-medium text-primary-500 uppercase tracking-wide">Featured</span>
        </div>
      )}

      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4 flex-1">
          <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-muted/50 p-1">
            <Image
              src={job.logo}
              alt={`${job.company} logo`}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h3 className="text-lg font-bold tracking-tight text-text-primary mb-1 hover:text-primary-600 transition-smooth">
                  {job.title}
                </h3>
                <p className="text-text-secondary font-medium">{job.company}</p>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSave();
                }}
                className={`p-2 rounded-lg transition-smooth ${isSaved
                  ? 'text-error bg-error-50 hover:bg-error-100' : 'text-text-secondary hover:text-error hover:bg-error-50'
                  }`}
              >
                <Icon name={isSaved ? "Heart" : "Heart"} size={20} fill={isSaved ? "currentColor" : "none"} />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary mb-3">
              <div className="flex items-center space-x-1">
                <Icon name="MapPin" size={16} />
                <span>{job.location}</span>
              </div>

              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={16} />
                <span>{job.employmentType}</span>
              </div>

              {job.remoteWork && (
                <div className="flex items-center space-x-1">
                  <Icon name="Wifi" size={16} />
                  <span>Remote</span>
                </div>
              )}

              <div className="flex items-center space-x-1">
                <span>{job.salaryRange}</span>
              </div>
            </div>

            <p className="text-text-secondary text-sm line-clamp-2 mb-4">
              {job.description.split('\n')[0]}
            </p>



            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <span className="text-xs text-text-secondary order-2 sm:order-1">
                Posted {formatTimeAgo(job.postedDate)}
              </span>

              <div className="flex items-center space-x-2 order-1 sm:order-2 w-full sm:w-auto">
                <Link
                  to={`/job-detail-application/${job.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 sm:flex-none text-center px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 border border-primary-600 hover:border-primary-700 rounded-lg transition-smooth"
                >
                  View Details
                </Link>

                {job.application_url ? (
                  <button
                    onClick={handleExternalApply}
                    className="flex-1 sm:flex-none text-center px-4 py-2 text-sm font-bold bg-primary text-primary-foreground hover:opacity-90 rounded-lg transition-smooth"
                  >
                    Apply Now
                  </button>
                ) : (
                  <Link
                    to={`/job-detail-application/${job.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 sm:flex-none text-center px-4 py-2 text-sm font-bold bg-primary text-primary-foreground hover:opacity-90 rounded-lg transition-smooth"
                  >
                    Apply Now
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobCard;